import React, { useEffect, useMemo, useState } from "react";
import {
  listOrders,
  getOrder,
  patchOrder,
  cancelOrder,
  shipOrder,
  deliverOrder,
  exportCsv,
} from "../services/AdminOrderService";
import "./AdminOrder.css";
import { toast } from "react-toastify";

const StatusTag = ({ s }) => (
  <span className={`tag ${String(s).toLowerCase()}`}>{s}</span>
);

// 📎 Helper: download invoice PDF
async function downloadInvoice(orderId) {
  const token = localStorage.getItem("token");
  if (!token) {
    toast.error("Please login to download invoice");
    return;
  }
  const res = await fetch(`/api/admin/orders/${orderId}/invoice.pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    toast.error("Failed to download invoice");
    return;
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoice-${orderId}.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default function AdminOrders() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [method, setMethod] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [drawer, setDrawer] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listOrders({ q, status, method, page, limit });
      setRows(data.rows || []);
      setCount(data.count || 0);
    } catch (e) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page, status, method]);

  const pages = useMemo(() => Math.max(1, Math.ceil(count / limit)), [count, limit]);

  const openOrder = async (id) => {
    try {
      const o = await getOrder(id);
      setDrawer(o);
    } catch (e) {
      toast.error("Failed to fetch order details");
    }
  };

  const doExport = async () => {
    try {
      const blob = await exportCsv({ q, status, method });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to export CSV");
    }
  };

  const updateField = async (patch) => {
    try {
      const o = await patchOrder(drawer.id, patch);
      setDrawer(o);
      await load();
    } catch (e) {
      toast.error("Failed to update order");
    }
  };

  return (
    <div className="admin-wrap">
      <header className="admin-header">
        <h2>Orders (PhonePe + COD)</h2>

        <div className="filters">
          <input
            placeholder="Search ref / name / mobile"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="CREATED">CREATED</option>
            <option value="PENDING">PENDING</option>
            <option value="SUCCESS">SUCCESS</option>
            <option value="FAILED">FAILED</option>
            <option value="SHIPPED">SHIPPED</option>
            <option value="DELIVERED">DELIVERED</option>
            <option value="CANCELLED">CANCELLED</option>
          </select>

          <select
            value={method}
            onChange={(e) => {
              setMethod(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Any Method</option>
            <option value="PHONEPE">PhonePe</option>
            <option value="COD">COD</option>
          </select>

          <button className="btn" onClick={load} disabled={loading}>
            Search
          </button>
          <button className="btn outline" onClick={doExport}>
            Export CSV
          </button>
        </div>
      </header>

      <div className="card">
        <table className="tbl">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Status</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Customer</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.merchantOrderId}</strong></td>
                <td><StatusTag s={r.state} /></td>
                <td>₹{Number(r.totalAmountRupees).toFixed(2)}</td>
                <td>{r.paymentMethod}</td>
                <td>{r.customerName || r.userId || "-"}</td>
                <td>
                  <button className="link" onClick={() => openOrder(r.id)}>
                    View
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan="6" className="empty">
                  {loading ? "Loading…" : "No orders"}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="pager">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </button>
          <span>
            Page {page} / {pages}
          </span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      </div>

      {/* Drawer */}
      {!!drawer && (
        <div className="drawer">
          <div className="drawer-card">
            <div className="drawer-head">
              <div>
                <h3>Order #{drawer.merchantOrderId}</h3>
                <p className="order-customer">Customer: <strong>{drawer.customerName || "N/A"}</strong></p>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {["SUCCESS", "SHIPPED", "DELIVERED"].includes(drawer.state) && (
                  <button
                    className="btn outline"
                    onClick={() => downloadInvoice(drawer.id)}
                  >
                    Download Invoice
                  </button>
                )}
                <button className="icon" onClick={() => setDrawer(null)}>✕</button>
              </div>
            </div>

            <div className="grid">
              <div>
                <h4>Payment</h4>
                <div className="kv"><span>Status</span> <StatusTag s={drawer.state} /></div>
                <div className="kv"><span>Method</span> {drawer.paymentMethod}</div>
                <div className="kv">
                  <span>Redirect URL</span>
                  <a href={drawer.redirectUrl} target="_blank" rel="noreferrer">
                    {drawer.redirectUrl ? "Open" : "-"}
                  </a>
                </div>
              </div>

              <div>
                <h4>Amounts</h4>
                <div className="kv">
                  <span>Total (₹)</span> <strong>₹{Number(drawer.totalAmountRupees).toFixed(2)}</strong>
                </div>

                <h4 style={{ marginTop: 16 }}>Customer Details</h4>
                <div className="kv"><span>User ID</span> {drawer.userId || "-"}</div>
                <div className="kv"><span>Full Name</span> {drawer.customerName || "-"}</div>
                <div className="kv"><span>Mobile</span> {drawer.customerMobile || "-"}</div>
                <div className="kv"><span>Address</span> {drawer.customerAddress || "-"}</div>
                <div className="kv"><span>Pincode</span> {drawer.customerPincode || "-"}</div>
              </div>

              <div>
                <h4>Actions</h4>
                <div className="row-actions">
                  <button 
                    onClick={() => shipOrder(drawer.id)
                      .then(() => toast.success("Order marked as shipped"))
                      .then(() => load())
                      .then(() => openOrder(drawer.id))
                      .catch(e => toast.error("Failed to ship order"))}
                  >
                    Mark Shipped
                  </button>
                  <button 
                    onClick={() => deliverOrder(drawer.id)
                      .then(() => toast.success("Order marked as delivered"))
                      .then(() => load())
                      .then(() => openOrder(drawer.id))
                      .catch(e => toast.error("Failed to deliver order"))}
                  >
                    Mark Delivered
                  </button>
                  <button 
                    onClick={() => cancelOrder(drawer.id)
                      .then(() => toast.success("Order cancelled"))
                      .then(() => load())
                      .then(() => openOrder(drawer.id))
                      .catch(e => toast.error("Failed to cancel order"))}
                    className="danger"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            {/* 🛍️ ORDER ITEMS SECTION */}
            <div className="order-items-section">
              <h4>📦 Order Items</h4>
              {drawer.cartItems && drawer.cartItems.length > 0 ? (
                <div className="order-items-table">
                  <table className="items-tbl">
                    <thead>
                      <tr>
                        <th>Product ID</th>
                        <th>Product Name</th>
                        <th>Quantity</th>
                        <th>Price (₹)</th>
                        <th>Total (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drawer.cartItems.map((item, idx) => (
                        <tr key={idx}>
                          <td><code>{item.productId || item.id || "-"}</code></td>
                          <td className="product-name">{item.name || item.productName || "-"}</td>
                          <td className="center"><strong>{item.quantity || 1}</strong></td>
                          <td className="right">₹{Number(item.price || 0).toFixed(2)}</td>
                          <td className="right"><strong>₹{Number((item.price || 0) * (item.quantity || 1)).toFixed(2)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="total-row">
                        <td colSpan="4" style={{ textAlign: "right" }}>
                          <strong>Total:</strong>
                        </td>
                        <td className="right">
                          <strong>₹{Number(drawer.totalAmountRupees).toFixed(2)}</strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="empty-message">No items in this order</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
